// @TODO: Commented for the time being until rewards are implemented
// import { RerwardSelector } from '../../../rewards/containers/reward-selector/reward-selector';
import type { ReactNode } from 'react';

import { BannerCollection } from '../../../shared/components/banner-collection/banner-collection';
import { Checkbox } from '../../../shared/components/checkbox/checkbox';
import { ContextLink } from '../../../shared/components/context-link/context-link';
import { Dropdown } from '../../../shared/components/dropdown/dropdown';
import { DisplayType } from '../../../shared/components/form-builder/form-builder';
import { FormattedMessage } from '../../../shared/components/formatted-message/formatted-message';
import { Input } from '../../../shared/components/input/input';
import { SlideType } from '../../../shared/components/slide/slide.interface';
import { createClassName, createStyleHelper } from '../../../shared/utils/class-names';
import { userCountryList } from '../../../shared/utils/countries';
import { userPhonePrefixList } from '../../../shared/utils/phone-prefix';
import { regionsList } from '../../../shared/utils/regions';

import styles from './registration-flow.module.scss';

const style = createStyleHelper(styles, 'registration-flow');

function createSimpleField<T extends string>({
    name,
    info,
    placeholder,
    label,
    type,
    classVariants,
    maxLen,
    pattern,
}: {
    name: T;
    info?: string;
    placeholder?: string;
    label?: string;
    type?: string;
    maxLen?: number;
    pattern?: string;
    classVariants?: string[];
}) {
    return {
        name,
        type: DisplayType.FIELD,
        label,
        className: style('field', classVariants),
        component: Input,
        info,
        props: {
            placeholder,
            type: type ?? 'text',
            maxLen,
        },
    } as const;
}

export const USERNAME_FIELD = createSimpleField({
    name: 'username',
    placeholder: 'registration.step-one.username.placeholder',
    label: '',
    info: 'registration.step-one.username.info',
    maxLen: 20,
});

export const MARKETING_CAMPAIGN = createSimpleField({
    name: 'marketing_campaign',
    placeholder: 'registration.step-three.marketing-campaign.placeholder',
    label: 'Referral Code',
    info: 'registration.step-three.marketing-campaign.info',
    maxLen: 50,
});

export const GIVEN_NAME_FIELD = createSimpleField({
    name: 'givenName',
    placeholder: 'registration.step-one.given-name.placeholder',
    maxLen: 32,
});

export const FAMILY_NAME_FIELD = createSimpleField({
    name: 'familyName',
    placeholder: 'registration.step-one.family-name.placeholder',
    maxLen: 32,
});

export const BIRTH_DATE_FIELD = createSimpleField({
    name: 'birthDate',
    label: 'registration.step-one.birth-date.label',
    type: 'date',
});

export const STREET_ADDRESS_FIELD = createSimpleField({
    name: 'addrStreetAddress',
    label: 'Address',
    placeholder: 'registration.step-one.street-address.placeholder',
    maxLen: 120,
});

export const POSTAL_CODE_FIELD = createSimpleField({
    name: 'addrPostalCode',
    placeholder: 'registration.step-one.postal-code.placeholder',
    maxLen: 5,
});

export const LOCALITY_FIELD = createSimpleField({
    name: 'addrLocality',
    placeholder: 'registration.step-one.locality.placeholder',
    maxLen: 120,
});

export const REGION_FIELD = {
    name: 'addrRegion' as const,
    className: style('field'),
    type: DisplayType.FIELD,
    component: Dropdown,
    props: {
        placeholder: 'registration.step-one.region.placeholder',
        items: regionsList,
    },
};

export const ADDRESS_COUNTRY_FIELD = {
    name: 'addrCountryCode' as const,
    className: style('field'),
    type: DisplayType.FIELD,
    component: Dropdown,
    props: {
        placeholder: 'registration.step-one.address-country.placeholder',
        items: userCountryList,
    },
};

export const EMAIL_FIELD = createSimpleField({
    name: 'email',
    placeholder: 'registration.step-one.email.placeholder',
});

export const TEL_PREFIX_FIELD = {
    name: 'telephonePrefix' as const,
    className: style('field', ['size-small']),
    type: DisplayType.FIELD,
    component: Dropdown,
    props: {
        items: userPhonePrefixList,
    },
};

export const TEL_FIELD = createSimpleField({
    name: 'telephone',
    classVariants: ['section'],
    type: 'tel',
    pattern: '[0-9]{3}[0-9]{3}[0-9]{4}',
    maxLen: 10,
    placeholder: 'registration.step-one.telephone.placeholder',
});

export const PASSWORD_FIELD = createSimpleField({
    name: 'password',
    placeholder: 'registration.step-one.password.placeholder',
    info: 'registration.step-one.password.info',
    type: 'password',
    maxLen: 32,
});

export const CONFIRM_PASSWORD_FIELD = createSimpleField({
    name: 'passwordConfirm',
    classVariants: ['section', 'section--last'],
    placeholder: 'registration.step-one.confirm-password.placeholder',
    type: 'password',
    maxLen: 32,
});

export const GENDER_FIELD = createSimpleField({
    name: 'gender',
    placeholder: 'registration.step-one.gender.placeholder',
});

export const TAX_ID_FIELD = createSimpleField({
    name: 'taxId',
    classVariants: ['section'],
    label: 'SSN',
    placeholder: 'registration.step-one.tax-id.placeholder',
    maxLen: 4,
});

export const TERMS_FIELD = {
    name: 'termsAndConditionsVersion' as const,
    className: style('checkbox', ['divider', 'divider--first']),
    type: DisplayType.FIELD,
    component: Checkbox,
    props: {
        label: (
            <span>
                <FormattedMessage
                    id="registration.step-one.terms-and-conditions.label"
                    values={{
                        'terms-link': (text: ReactNode) => (
                            <ContextLink
                                className={style('link')}
                                to="/documents/general-terms-of-service"
                                target="_blank"
                            >
                                {text}
                            </ContextLink>
                        ),
                        'privacy-policy-link': (text: ReactNode) => (
                            <ContextLink
                                className={style('link')}
                                to="/documents/privacy-policy"
                                target="_blank"
                            >
                                {text}
                            </ContextLink>
                        ),
                    }}
                />
            </span>
        ),
    },
} as const;

export const MARKETING_FIELD = {
    name: 'promo' as const,
    className: style('checkbox', ['divider', 'divider--last']),
    type: DisplayType.FIELD,
    component: Checkbox,
    props: {
        label: (
            <span>
                <FormattedMessage id="registration.step-one.promotional-offers.label" />
            </span>
        ),
        defaultValue: true,
    },
} as const;

// @TODO: Commented for the time being until rewards are implemented
// export const SELECT_REWARD_FIELD = {
//     name: 'reward' as const,
//     type: DisplayType.COMPONENT,
//     component: RerwardSelector,
//     props: {
//         className: style('reward'),
//     },
// };

export const BANNER_PRE_REGISTRATION = {
    name: 'banner-pre-registration' as const,
    type: DisplayType.COMPONENT,
    component: BannerCollection,
    props: {
        className: createClassName([style('registration'), style('banner')]),
        page: 'registration',
        type: SlideType.REGISTRATION,
    },
};

export const BANNER_COMPLETE_REGISTRATION = {
    name: 'banner-registration-complete' as const,
    type: DisplayType.COMPONENT,
    component: BannerCollection,
    props: {
        className: createClassName([style('registration'), style('banner')]),
        page: 'registration-complete',
        type: SlideType.REGISTRATION,
    },
};

export const SCHEMA_1_CONSTANTS = {
    CONFIRM_PASSWORD_FIELD: CONFIRM_PASSWORD_FIELD.name,
    EMAIL_FIELD: EMAIL_FIELD.name,
    FAMILY_NAME_FIELD: FAMILY_NAME_FIELD.name,
    GIVEN_NAME_FIELD: GIVEN_NAME_FIELD.name,
    MARKETING_FIELD: MARKETING_FIELD.name,
    PASSWORD_FIELD: PASSWORD_FIELD.name,
    TEL_FIELD: TEL_FIELD.name,
    TEL_PREFIX_FIELD: TEL_PREFIX_FIELD.name,
    USERNAME_FIELD: USERNAME_FIELD.name,
    string: 'avoid-type-error',
};

export const SCHEMA_2_CONSTANTS = {
    ADDRESS_COUNTRY_FIELD: ADDRESS_COUNTRY_FIELD.name,
    REGION_FIELD: REGION_FIELD.name,
    BIRTH_DATE_FIELD: BIRTH_DATE_FIELD.name,
    POSTAL_CODE_FIELD: POSTAL_CODE_FIELD.name,
    STREET_ADDRESS_FIELD: STREET_ADDRESS_FIELD.name,
    LOCALITY_FIELD: LOCALITY_FIELD.name,
    TAX_ID_FIELD: TAX_ID_FIELD.name,
    string: 'avoid-type-error',
};

export const SCHEMA_3_CONSTANTS = {
    MARKETING_CAMPAIGN: MARKETING_CAMPAIGN.name,
    TERMS_FIELD: TERMS_FIELD.name,
    string: 'avoid-type-error',
};
